<!--
AppIcon Component

A reusable icon wrapper component using Iconify with the Solar icon set.
Provides consistent sizing presets and optional spin animation.

Usage:
```vue
<AppIcon name="solar:sun-bold" />
<AppIcon name="solar:moon-bold" size="lg" />
<AppIcon name="solar:spinner-bold" spin />
```

Size Presets:
- xs: 0.75rem (12px)
- sm: 1rem (16px)
- md: 1.25rem (20px) - default
- lg: 1.5rem (24px)
- xl: 1.75rem (28px)
- 2xl: 2rem (32px)
- 3xl: 2.5rem (40px)
- 4xl: 3rem (48px)
- 5xl: 3.5rem (56px)
- 6xl: 4rem (64px)

The icon inherits the current text color and can be styled with Tailwind classes.
-->

<script setup lang="ts">
import { Icon } from '@iconify/vue'
import { computed } from 'vue'

type SizePreset = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl'

interface Props {
  /**
   * The icon name in Iconify format (e.g., 'solar:sun-bold')
   */
  name: string
  /**
   * Size preset for the icon
   */
  size?: SizePreset
  /**
   * Whether the icon should spin (useful for loading indicators)
   */
  spin?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  spin: false,
})

const sizeClasses: Record<SizePreset, string> = {
  xs: 'tw-w-3 tw-h-3',
  sm: 'tw-w-4 tw-h-4',
  md: 'tw-w-5 tw-h-5',
  lg: 'tw-w-6 tw-h-6',
  xl: 'tw-w-7 tw-h-7',
  '2xl': 'tw-w-8 tw-h-8',
  '3xl': 'tw-w-10 tw-h-10',
  '4xl': 'tw-w-12 tw-h-12',
  '5xl': 'tw-w-14 tw-h-14',
  '6xl': 'tw-w-16 tw-h-16',
}

const iconClass = computed(() => [
  sizeClasses[props.size],
  props.spin ? 'tw-animate-spin' : '',
])
</script>

<template>
  <Icon :icon="props.name" :class="iconClass" aria-hidden="true" />
</template>
